import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, fullName, formatDate } from '../lib/api';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Eye, Stethoscope, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import PdfPreviewDialog from '../components/PdfPreviewDialog';
import PdfLangButton from '../components/PdfLangButton';
import { PatientCombobox } from '../components/PatientCombobox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function NewPreopDialog({ open, onOpenChange, onCreated, initialPatient }) {
  const [patient_id, setPatientId] = useState(initialPatient || '');
  const [custom_notes, setCustomNotes] = useState('');
  useEffect(() => {
    if (open) {
      setPatientId(initialPatient || '');
      setCustomNotes('');
    }
  }, [open, initialPatient]);
  const submit = async () => {
    if (!patient_id) return toast.error('Selecciona paciente');
    try {
      const res = await api.post('/api/preop', { patient_id, custom_notes });
      toast.success('Solicitud creada');
      onCreated && onCreated(res.data);
      onOpenChange(false);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Generar solicitud de pruebas preoperatorias</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground bg-[rgba(46,110,94,0.08)] p-3 rounded">
            Se genera una <strong>solicitud</strong> para que el paciente realice <strong>Analítica sanguínea</strong> (bioquímica, hemograma y coagulación) y <strong>Electrocardiograma (ECG)</strong>. Validez de las pruebas: 3 meses.
          </div>
          <div>
            <Label>Paciente</Label>
            <PatientCombobox value={patient_id} onChange={setPatientId} dataTestid="preop-patient-select" />
          </div>
          <div>
            <Label>Notas (opcional)</Label>
            <Textarea value={custom_notes} onChange={(e) => setCustomNotes(e.target.value)} placeholder="Indicaciones adicionales para el laboratorio…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="preop-create-button">Crear</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PreopPage() {
  const [searchParams] = useSearchParams();
  const initialPatient = searchParams.get('patient');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(!!initialPatient);
  const [preview, setPreview] = useState({ open: false, url: '', filename: '', title: '' });
  const fetchItems = async () => { setLoading(true); try { const r = await api.get('/api/preop'); setItems(r.data); } finally { setLoading(false); } };
  useEffect(() => { fetchItems(); }, []);
  const onDelete = async (id) => { if (!window.confirm('¿Eliminar?')) return; await api.delete(`/api/preop/${id}`); toast.success('Eliminado'); fetchItems(); };

  return (
    <div>
      {/* «Pruebas preoperatorias», no «volantes» (dirección, 2026-09-03): el
          mismo nombre que la pestaña de la ficha y que el paso 2 de la guía. */}
      <PageHeader title="Pruebas preoperatorias" subtitle={`${items.length} solicitudes generadas (analítica + ECG)`}
        actions={<Button onClick={() => setOpen(true)} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="preop-new-button"><Plus className="w-4 h-4 mr-2" /> Nueva solicitud</Button>} />
      <div className="cjo-card overflow-hidden">
        {loading ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
          : items.length === 0 ? <EmptyState icon={Stethoscope} title="Sin solicitudes" description="Genera la primera solicitud de pruebas." />
          : <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-[rgba(46,110,94,0.04)] text-left"><tr><th className="py-3 px-4 font-semibold text-[#13231E]">Paciente</th><th className="py-3 px-4 font-semibold text-[#13231E]">Notas</th><th className="py-3 px-4 font-semibold text-[#13231E]">Generado</th><th></th></tr></thead>
            <tbody>{items.map(po => <tr key={po.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="preop-row">
              <td className="py-3 px-4 font-medium">{po.patient_name}</td>
              <td className="py-3 px-4 text-xs truncate max-w-[300px]">{po.custom_notes || '—'}</td>
              <td className="py-3 px-4 text-xs">{formatDate(po.created_at)}</td>
              <td className="py-3 px-4 text-right"><div className="flex gap-1 items-center justify-end">
                <PdfLangButton
                  baseUrl={`${BACKEND_URL}/api/preop/${po.id}/pdf`}
                  filename={`preoperatorio_${po.id}.pdf`}
                  title={`Solicitud de pruebas preoperatorias · ${po.patient_name}`}
                  onOpen={(p) => setPreview({ open: true, ...p })}
                  dataTestid="preop-pdf-button"
                >
                  <Button size="sm" variant="outline" title="Ver preoperatorio (elige idioma)"><Eye className="w-4 h-4" /></Button>
                </PdfLangButton>
                <Button size="sm" variant="ghost" onClick={() => onDelete(po.id)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div></td>
            </tr>)}</tbody></table></div>}
      </div>
      <NewPreopDialog open={open} onOpenChange={setOpen} initialPatient={initialPatient} onCreated={fetchItems} />
      <PdfPreviewDialog open={preview.open} onOpenChange={(v) => setPreview(s => ({ ...s, open: v }))} url={preview.url} filename={preview.filename} title={preview.title} />
    </div>
  );
}
